"use client";

import { useState, useEffect, useRef } from "react";

/** Animated number that counts from previous value to target with ease-out cubic */
export default function AnimatedNumber({ value, decimals = 0 }: { value: number; decimals?: number }) {
  const [display, setDisplay] = useState(0);
  const prevValue = useRef(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (value === prevValue.current) return;
    const start = prevValue.current;
    const diff = value - start;
    const duration = 800;
    const startTime = performance.now();

    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + diff * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        prevValue.current = value;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  // 2026-05-22 (Pika): force en-US so a Spanish browser doesn't
  // start rendering "$10,04" while the rest of the product (which
  // goes through lib/format.ts → formatCurrency with `en-US`) keeps
  // emitting "$10.04". Pika reported the inconsistency on
  // /portfolio where the header delta was coma-decimal and every
  // other number was point-decimal. Single source of truth: en-US
  // matches formatCurrency. If we ever localise number formats,
  // both this helper and formatCurrency get updated together.
  if (decimals > 0) {
    return <>{display.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
  }
  return <>{Math.round(display).toLocaleString("en-US")}</>;
}
